// v4 refinery — a still that cooks raw wood into fuel: a steel boiler with
// hazard bands and a fire box, a copper condenser column and pipe, a dark
// chimney trailing smoke, and fuel barrels out front. The v4 polish uses a
// dedicated COPPER colour for the column instead of the ROOF_HOME terracotta
// the v3 borrowed.
//
// Ported 1:1 from age-of-mcp's v4/refinery.ts, using the library's shared
// v3 style helpers (`styleV3`).
import { defineModel, benchWork, styleV3, type Part } from "../../lib";

const { box, cyl, poles, slab, CANVAS, FIRE, HAZARD, ROOF_LOGI, STEEL } = styleV3;

// Copper: visually identical to the v3 ROOF_HOME choice, but labelled for
// what it is — a metal, not a roof colour code.
const COPPER = "#b87333";

const BASE = 0.12;

function boiler(): Part[] {
  return [
    cyl(0.19, 0.21, 0.4, 10, STEEL, [-0.07, BASE + 0.2, 0.03]),
    cyl(0.195, 0.195, 0.03, 10, HAZARD, [-0.07, BASE + 0.1, 0.03]),
    cyl(0.195, 0.195, 0.03, 10, HAZARD, [-0.07, BASE + 0.32, 0.03]),
    box(0.15, 0.1, 0.05, FIRE, [-0.07, BASE + 0.06, 0.22]),
  ];
}

function column(): Part[] {
  return [
    cyl(0.07, 0.08, 0.58, 8, COPPER, [0.18, BASE + 0.29, -0.06]),
    cyl(0.085, 0.085, 0.05, 8, HAZARD, [0.18, BASE + 0.58, -0.06]),
    cyl(0.025, 0.025, 0.28, 6, COPPER, [0.06, BASE + 0.45, -0.02], [0, 0, Math.PI / 2.4]),
  ];
}

function chimney(): Part[] {
  return [
    box(0.1, 0.44, 0.1, HAZARD, [-0.22, BASE + 0.34, -0.17]),
    box(0.12, 0.1, 0.12, CANVAS, [-0.22, BASE + 0.61, -0.17]),
  ];
}

function barrels(): Part[] {
  return [
    cyl(0.06, 0.06, 0.15, 7, ROOF_LOGI, [0.17, BASE + 0.075, 0.18]),
    cyl(0.063, 0.063, 0.03, 7, HAZARD, [0.17, BASE + 0.075, 0.18]),
    cyl(0.06, 0.06, 0.15, 7, ROOF_LOGI, [0.07, BASE + 0.075, 0.32], [0, 0, Math.PI / 2]),
  ];
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(0.5, 0.5)];
  if (s === 1) {
    return [
      slab(0.5, 0.5),
      cyl(0.19, 0.21, 0.14, 10, STEEL, [-0.07, BASE + 0.07, 0.03]),
      ...poles(0.44, 0.44, 0.5),
    ];
  }
  if (s === 2) return [slab(0.5, 0.5), ...boiler(), ...chimney(), ...poles(0.44, 0.44, 0.8)];
  return [slab(0.5, 0.5), ...boiler(), ...column(), ...chimney(), ...barrels()];
}

export const refineryV4 = defineModel({
  id: "refinery-v4",
  tiles: { w: 2, h: 2 },
  batchId: "polished",
  name: "Refinery (v4)",
  tags: ["building","refinery","low-poly","age-of-mcp","v4"],
  summary: "Low-poly refinery.",
  description: "A still that cooks raw wood into fuel.",
  author: "age-of-mcp",
  footprint: 0.42,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "bench", position: [-0.07, 0.12, 0.42], facing: Math.PI },
  ],
  interactions: [
    { id: "refine", label: "At the still", station: "bench", variants: ["complete"], ...benchWork() },
  ],
});
